import type { ITranslations } from '@inter-ikea-kompis/types';
import type { CacheStore } from '@inter-ikea-kompis/utilities';
import { GlobalCacheStore } from '@inter-ikea-kompis/utilities';

import CacheStoreType from '../../enums/CacheStoreType.js';
import InternalTranslationsService from '../../services/localization/InternalTranslationsService.js';
import type ITranslationsServiceOptions from './ITranslationsServiceOptions.js';

/**
 * This class is used as a simple interface for translation services.
 */
export default class TranslationsService<T extends ITranslations> {
	private readonly translationPrefixApplication: string | undefined;
	private readonly translationEndpoints: string[];
	private readonly locale: string;
	private readonly cache: CacheStore;

	/**
	 * Constructor.
	 *
	 * @param options Options.
	 */
	constructor(options: ITranslationsServiceOptions) {
		this.translationPrefixApplication = options.translationPrefixApplication;
		this.translationEndpoints = options.translationEndpoints;
		this.locale = options.locale;
		this.cache = options.cache || new GlobalCacheStore();
	}

	/**
	 * Fetches and returns translations.
	 *
	 * @returns Promise object.
	 */
	public async getTranslations(): Promise<T> {
		const cacheStore = this.cache.getStore(CacheStoreType.translations);
		const endpointsCacheKey = JSON.stringify(this.translationEndpoints);
		const translationsCacheKey = this.locale + endpointsCacheKey;
		const queueCacheKey = this.locale + endpointsCacheKey + 'queue';

		const cachedTranslations = cacheStore.get(translationsCacheKey);
		if (cachedTranslations) {
			return cachedTranslations;
		}

		const cachedQueue = cacheStore.get(queueCacheKey);
		if (cachedQueue) {
			return new Promise((resolve, reject) => cachedQueue.push({ resolve, reject }));
		}

		const newQueue: {
			resolve: (data: T) => void;
			reject: (data: string) => void;
		}[] = [];
		cacheStore.set(queueCacheKey, newQueue);

		const service = new InternalTranslationsService<T>({
			translationPrefixApplication: this.translationPrefixApplication,
			translationEndpoints: this.translationEndpoints,
			locale: this.locale
		});

		let translations: T;
		try {
			translations = await service.getTranslations();
		} catch (error) {
			for (const item of newQueue) {
				item.reject(error);
			}
			throw error;
		}

		cacheStore.set(translationsCacheKey, translations);
		cacheStore.delete(queueCacheKey);

		for (const item of newQueue) {
			item.resolve(translations);
		}

		return translations;
	}
}
